import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import RichTextEditor from '../components/RichTextEditor';
import { formatAnswer } from '../utils/answerFormatter';
import WhisperTranscriber from '../utils/whisperTranscriber';
import '../styles/Dashboard.css';

// Configure API URL for development/production
// In development: http://localhost:5001
// In production: use current domain (https)
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? window.location.origin
  : process.env.REACT_APP_API_URL || 'http://localhost:5001';

function Dashboard({ user, onLogout, profile, interviewContext }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [question, setQuestion] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMyQuestions, setShowMyQuestions] = useState(false);
  const [formData, setFormData] = useState({ question: '', answer: '' });
  const [editingId, setEditingId] = useState(null);
  const [myQuestions, setMyQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [answerTextRef, setAnswerTextRef] = useState(null);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('activeSessionId') || null);
  const [shareUrl, setShareUrl] = useState(() => localStorage.getItem('activeShareUrl') || null);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [isStartingSession, setIsStartingSession] = useState(false);
  const token = localStorage.getItem('token');
  const [starredQuestions, setStarredQuestions] = useState([]);
  const [starNewQuestion, setStarNewQuestion] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [starredExpanded, setStarredExpanded] = useState(false);
  
  // Chat Feature State
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatVoiceListening, setChatVoiceListening] = useState(false);
  const chatMessagesEndRef = React.useRef(null);
  const chatMediaRecorderRef = React.useRef(null);

  // Voice Search Setup
  const mediaRecorderRef = React.useRef(null);
  const socketRef = React.useRef(null);

  // Detect mobile vs desktop
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize Socket.IO for live sessions
  useEffect(() => {
    // Only connect if we have a session ID
    if (!sessionId) {
      console.log('⏳ No active session yet, will connect when session is created');
      if (socketRef.current?.connected) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    console.log('🔌 Attempting to connect to WebSocket for session:', sessionId);

    const socket = io(API_BASE_URL || 'http://localhost:5001', {
      query: {
        sessionId: sessionId,
        role: 'trainer'
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5
    });

    socket.on('connect', () => {
      console.log('✓ Connected to WebSocket server as trainer for session:', sessionId);
      console.log('   Socket ID:', socket.id);
      console.log('   Socket ready for broadcasting');
    });

    socket.on('disconnect', () => {
      console.log('⚠ Disconnected from WebSocket server');
    });

    socket.on('error', (error) => {
      console.error('Socket error:', error);
    });

    socketRef.current = socket;

    return () => {
      console.log('🔌 Cleaning up WebSocket connection');
      socket.disconnect();
    };
  }, [sessionId]);

  // Restore session from server if localStorage is empty but trainer has active session
  useEffect(() => {
    const restoreSession = async () => {
      const existingSessionId = localStorage.getItem('activeSessionId');
      if (existingSessionId) return; // Already have a session in localStorage
      
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/sessions/trainer/active`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.success && response.data.data) {
          const { sessionId: activeId, shareUrl: activeUrl } = response.data.data;
          console.log('✓ Restored active session from server:', activeId);
          localStorage.setItem('activeSessionId', activeId);
          localStorage.setItem('activeShareUrl', activeUrl);
          setSessionId(activeId);
          setShareUrl(activeUrl);
        }
      } catch (err) {
        // No active session - that's fine
      }
    };
    
    if (token) {
      restoreSession();
    }
  }, [token]);

  // Set initial page title
  useEffect(() => {
    document.title = 'Find QA Answers - FQA';
    // Clear any previous question on page load
    setQuestion(null);
    setSuggestions([]);
    setSearchQuery('');
  }, []);

  // Note: No longer updating URL with query parameters
  // Searches are managed in state only, keeping URL clean
  const updateSearchURL = (query) => {
    // Intentionally skipping URL updates - keeping URL clean
  };

  // Fetch suggestions with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        fetchSuggestions(searchQuery);
      } else {
        setSuggestions([]);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load question from URL on mount
  useEffect(() => {
    // No longer loading from URL - keeping URL clean
    // Question selection is done via search results only
  }, []);

  // Clear search on page refresh
  useEffect(() => {
    // Search state is managed in component, no URL params to clear
  }, []);

  const fetchSuggestions = async (query) => {
    try {
      // Don't show loading for suggestions - only for actual question fetch
      const params = new URLSearchParams({
        q: query,
        limit: 10
      });

      // Add interview context filters if available
      if (interviewContext) {
        params.append('companyName', interviewContext.companyName);
        params.append('interviewRound', interviewContext.interviewRound);
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/search?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuggestions(response.data.data || []);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      
      // Check for invalid token
      if (handleInvalidToken(err)) return;
      
      // Log error details
      const errorMsg = err.response?.data?.message || err.message || 'Search error';
      console.error('❌ Search error:', errorMsg);
      
      setSuggestions([]);
    }
  };

  const fetchQuestion = async (q) => {
    try {
      setIsLoading(true);
      console.log('🔍 Fetching question:', q);
      
      const response = await axios.get(
        `${API_BASE_URL}/api/question/${encodeURIComponent(q)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const questionData = response.data.data;
      setQuestion(questionData);
      setSuggestions([]);
      
      console.log('✅ Question fetched successfully:', {
        question: questionData.question,
        hasAnswer: !!questionData.answer,
        answerLength: questionData.answer?.length || 0
      });

      // Broadcast selected question to session if active
      console.log('📊 Checking broadcast conditions:', {
        sessionId: sessionId,
        socketExists: !!socketRef.current,
        socketConnected: socketRef.current?.connected,
        socketId: socketRef.current?.id
      });
      
      if (sessionId && socketRef.current?.connected) {
        console.log('📡 Broadcasting selected question to session:', sessionId);
        socketRef.current.emit('trainer:update-answer', {
          question: questionData.question,
          answer: questionData.answer
        });
        console.log('✅ Broadcast emitted');
      } else {
        console.warn('⚠️ Cannot broadcast:', {
          reason: !sessionId ? 'No session ID' : !socketRef.current ? 'No socket' : 'Socket not connected',
          sessionId,
          socketExists: !!socketRef.current,
          socketConnected: socketRef.current?.connected
        });
      }
    } catch (err) {
      console.error('Error fetching question:', err);
      if (handleInvalidToken(err)) return;
      setQuestion(null);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMyQuestions = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/questions/my`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMyQuestions(response.data.data || []);
      setShowMyQuestions(true);
    } catch (err) {
      console.error('Error fetching questions:', err);
      if (handleInvalidToken(err)) return;
      
      // Show error message
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load questions';
      console.error('❌ My Questions error:', errorMsg);
      alert(`Error loading questions: ${errorMsg}`);
      
      setMyQuestions([]);
      setShowMyQuestions(false);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStarredQuestions = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/questions/starred/list`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStarredQuestions(response.data.data || []);
    } catch (err) {
      console.error('Error fetching starred questions:', err);
      if (handleInvalidToken(err)) return;
    }
  };

  const toggleQuestionStar = async (id, event) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      // Optimistic update - update UI immediately
      const isCurrentQuestion = question && question.id === id;
      const newStarredState = isCurrentQuestion ? !question.is_starred : !(myQuestions.find(q => q.id === id)?.is_starred);
      
      // Update question details if viewing
      if (isCurrentQuestion) {
        setQuestion({
          ...question,
          is_starred: newStarredState
        });
      }
      
      // Update in My Questions list
      setMyQuestions(myQuestions.map(q => 
        q.id === id ? { ...q, is_starred: newStarredState } : q
      ));
      
      // Make API call
      const response = await axios.put(
        `${API_BASE_URL}/api/questions/${id}/star`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh starred questions sidebar
      fetchStarredQuestions();
      
    } catch (err) {
      console.error('Error toggling star:', err);
      if (handleInvalidToken(err)) return;
      // Revert on error by refreshing
      fetchMyQuestions();
      fetchStarredQuestions();
    }
  };

  // Load starred questions on mount
  useEffect(() => {
    fetchStarredQuestions();
    fetchCustomPrompt();
  }, []);

  // Fetch custom prompt
  const fetchCustomPrompt = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/ai/prompt`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomPrompt(response.data.data.customPrompt || '');
    } catch (err) {
      console.error('Error fetching custom prompt:', err);
      if (handleInvalidToken(err)) return;
    }
  };

  // Save custom prompt
  const saveCustomPrompt = async () => {
    if (!customPrompt.trim()) {
      alert('Please enter a custom prompt');
      return;
    }
    try {
      await axios.post(
        `${API_BASE_URL}/api/ai/prompt`,
        { customPrompt },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Custom prompt saved successfully!');
      setShowPromptEditor(false);
    } catch (err) {
      console.error('Error saving custom prompt:', err);
      if (handleInvalidToken(err)) return;
      alert('Error saving custom prompt. Please try again.');
    }
  };

  // Handle invalid token - auto logout ONLY for auth errors, not deployment/network issues
  const handleInvalidToken = (error) => {
    // Only logout if it's a real 401 Unauthorized error from backend
    // Don't logout on network errors, 502/503, or connection timeouts (these are deployment issues)
    if (error.response?.status === 401) {
      const errorMsg = error.response?.data?.message || '';
      // Only if it's explicitly an auth/token error
      if (errorMsg.includes('Invalid token') || errorMsg.includes('expired') || errorMsg.includes('Logged in elsewhere')) {
        console.log('🔴 Auth error detected - logging out');
        alert('Your session has expired. Please login again.');
        localStorage.removeItem('token');
        localStorage.clear();
        window.location.href = '/login';
        return true;
      }
    }
    // Don't logout on network errors, 5xx errors, or other temporary issues
    return false;
  };

  const saveQuestion = async () => {
    if (!formData.question || !formData.answer) {
      alert('Please fill in all fields');
      return;
    }
    try {
      setIsLoading(true);
      const questionData = {
        ...formData,
        companyName: interviewContext?.companyName || null,
        interviewRound: interviewContext?.interviewRound || null
      };
      console.log('Saving question:', questionData);
      const response = await axios.post(
        `${API_BASE_URL}/api/questions`,
        questionData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Save response:', response.data);
      
      // Star the question if checkbox is checked
      if (starNewQuestion) {
        try {
          await axios.put(
            `${API_BASE_URL}/api/questions/${response.data.id}/star`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          console.log('Question starred!');
        } catch (starErr) {
          console.error('Error starring question:', starErr);
        }
      }
      
      alert('Question saved successfully!');
      setFormData({ question: '', answer: '' });
      setStarNewQuestion(false);
      setShowAddModal(false);
      
      // Trigger event to refresh ProfileDetail
      window.dispatchEvent(new Event('questionAdded'));
      
      // Refresh starred questions list
      fetchStarredQuestions();
    } catch (err) {
      console.error('Error saving question:', err);
      if (handleInvalidToken(err)) {
        setIsLoading(false);
        return;
      }
      if (err.response) {
        alert(`Error: ${err.response.data.message || err.response.statusText}`);
      } else {
        alert('Error saving question. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const deleteQuestion = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      setIsLoading(true);
      await axios.delete(
        `${API_BASE_URL}/api/questions/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchMyQuestions();
    } catch (err) {
      console.error('Error deleting question:', err);
      if (handleInvalidToken(err)) {
        setIsLoading(false);
        return;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = () => {
    if (!question) return;
    setEditingId(question.id);
    setFormData({
      question: question.question,
      answer: question.answer
    });
    setShowEditModal(true);
  };

  const saveEditedQuestion = async () => {
    if (!formData.question || !formData.answer) {
      alert('Please fill in all fields');
      return;
    }
    try {
      setIsLoading(true);
      await axios.put(
        `${API_BASE_URL}/api/questions/${editingId}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Question updated successfully!');
      setShowEditModal(false);
      setEditingId(null);
      setFormData({ question: '', answer: '' });
      setQuestion({
        ...question,
        question: formData.question,
        answer: formData.answer
      });
    } catch (err) {
      console.error('Error updating question:', err);
      if (handleInvalidToken(err)) {
        setIsLoading(false);
        return;
      }
      alert('Error updating question. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteCurrentQuestion = async () => {
    if (!window.confirm('Delete this question?')) return;
    try {
      setIsLoading(true);
      await axios.delete(
        `${API_BASE_URL}/api/questions/${editingId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Question deleted successfully!');
      setShowEditModal(false);
      setEditingId(null);
      setQuestion(null);
      setSearchQuery('');
    } catch (err) {
      console.error('Error deleting question:', err);
      if (handleInvalidToken(err)) {
        setIsLoading(false);
        return;
      }
      alert('Error deleting question. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const insertHTMLTag = (openTag, closeTag = '') => {
    const textarea = document.querySelector('textarea[data-target="answer"]');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.answer.substring(start, end);
    const before = formData.answer.substring(0, start);
    const after = formData.answer.substring(end);

    const newText = before + openTag + selectedText + closeTag + after;
    setFormData({ ...formData, answer: newText });

    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = start + openTag.length;
      textarea.selectionEnd = start + openTag.length + selectedText.length;
    }, 0);
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    updateSearchURL(value);
    setQuestion(null); // Clear the answer when searching
  };

  const handleSearchMicDown = async () => {
    if (aiLoading || isListening) return;
    
    try {
      setQuestion(null);
      setSuggestions([]);
      const transcriber = new WhisperTranscriber();
      mediaRecorderRef.current = transcriber;
      
      await transcriber.startRecording();
      setIsListening(true);
    } catch (error) {
      console.error('❌ Microphone error:', error);
      alert(`Microphone error: ${error.message}`);
      setIsListening(false);
    }
  };

  const handleSearchMicUp = async () => {
    if (!isListening) return;

    try {
      setAiLoading(true);
      const audioBlob = await mediaRecorderRef.current.stopRecording();
      console.log('🔄 Transcribing audio with Whisper...');
      
      const transcribedText = await mediaRecorderRef.current.transcribeAudio(audioBlob, token);
      console.log(`✨ Transcribed: "${transcribedText}"`);
      
      // Update search field and search
      setSearchQuery(transcribedText);
      handleSearchChange(transcribedText);
      
      setIsListening(false);
      setAiLoading(false);
    } catch (error) {
      console.error('❌ Transcription failed:', error);
      alert(`Transcription error: ${error.message}`);
      setIsListening(false);
      setAiLoading(false);
    }
  };

  // Desktop: click to start/stop recording (toggle)
  const handleSearchMicClick = async () => {
    if (aiLoading) return;
    if (isListening) {
      await handleSearchMicUp();
    } else {
      await handleSearchMicDown();
    }
  };

  // Handle when user moves mouse/pointer away from button while holding (cancel recording - mobile only)
  const handleSearchMicLeave = async () => {
    if (isListening && mediaRecorderRef.current) {
      try {
        mediaRecorderRef.current.abortRecording();
        setIsListening(false);
      } catch (error) {
        console.error('❌ Error canceling recording:', error);
      }
    }
  };

  const correctAndSearch = async (rawText) => {
    try {
      setAiLoading(true);
      console.log(`📝 Raw transcript: "${rawText}"`);
      
      const response = await axios.post(
        `${API_BASE_URL}/api/rephrase`,
        { text: rawText },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.data.rephrased) {
        const correctedText = response.data.data.rephrased;
        console.log(`✨ Corrected: "${correctedText}"`);
        
        // Update search field with corrected text
        setSearchQuery(correctedText);
        
        // Trigger search
        handleSearchChange(correctedText);
      }
    } catch (error) {
      console.error('Correction error:', error);
      // Fallback: use raw text if correction fails
      console.log('Falling back to raw text...');
      setSearchQuery(rawText);
      handleSearchChange(rawText);
    } finally {
      setAiLoading(false);
    }
  };

  const insertFormatting = (before, after = '', formatType = 'simple') => {
    const textarea = document.querySelector('.form-textarea');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const selectedText = text.substring(start, end) || 'text';
    
    let newText;
    
    if (formatType === 'list' || formatType === 'ordered') {
      // Handle multi-line list formatting
      const lines = selectedText.split('\n');
      const formattedLines = lines.map((line, index) => {
        if (line.trim()) {
          return formatType === 'list' ? `- ${line}` : `${index + 1}. ${line}`;
        }
        return line;
      }).join('\n');
      newText = text.substring(0, start) + formattedLines + text.substring(end);
    } else {
      // Handle regular formatting
      newText = text.substring(0, start) + before + selectedText + after + text.substring(end);
    }
    
    setFormData({ ...formData, answer: newText });
    
    setTimeout(() => {
      textarea.focus();
      if (formatType === 'list' || formatType === 'ordered') {
        textarea.selectionStart = start;
        textarea.selectionEnd = start + selectedText.length + (selectedText.split('\n').length * 3);
      } else {
        textarea.selectionStart = start + before.length;
        textarea.selectionEnd = start + before.length + selectedText.length;
      }
    }, 0);
  };

  const autoCorrectQuery = (query) => {
    // Common misspellings and corrections
    const corrections = {
      // Selenium & Tools
      'selnium': 'selenium',
      'selinium': 'selenium',
      'tesng': 'testng',
      'testng': 'testng',
      'restapi': 'rest api',
      'rest api': 'rest api',
      'jva': 'java',
      'jaba': 'java',
      'phython': 'python',
      'pyhton': 'python',
      'javascrpt': 'javascript',
      'atuomation': 'automation',
      'automtion': 'automation',
      'automatoin': 'automation',
      'wedriver': 'webdriver',
      'webdrivr': 'webdriver',
      'framwork': 'framework',
      'framewrok': 'framework',
      
      // Test Concepts
      'testcase': 'test case',
      'testcases': 'test cases',
      'asserton': 'assertion',
      'asserion': 'assertion',
      'tst': 'test',
      'tsting': 'testing',
      'pge': 'page',
      'elment': 'element',
      'elemnt': 'element',
      
      // Locators (XPath, CSS, etc.)
      'locator': 'locator',
      'locatr': 'locator',
      'xpath': 'xpath',
      'xpth': 'xpath',
      'xpathe': 'xpath',
      'css selector': 'css selector',
      'css': 'css selector',
      'id': 'id',
      'class name': 'class name',
      'classname': 'class name',
      'tag name': 'tag name',
      'tagname': 'tag name',
      'link text': 'link text',
      'linktext': 'link text',
      
      // Compound terms
      'cell name': 'xpath',
      'cellname': 'xpath',
      'cell': 'xpath',
      'index': 'index locator',
      'selector': 'css selector',
      'name': 'name attribute',
      'by id': 'id',
      'by xpath': 'xpath',
      'by css': 'css selector',
      
      // Common QA terms
      'qa': 'quality assurance',
      'bdd': 'behavior driven development',
      'tdd': 'test driven development',
      'ci/cd': 'continuous integration',
      'api': 'api testing',
      
      // Framework & Methods
      'findElement': 'find element',
      'findelement': 'find element',
      'sendKeys': 'send keys',
      'sendkeys': 'send keys',
      'click': 'click',
      'getText': 'get text',
      'gettext': 'get text',
      'getAttribute': 'get attribute',
      'getattribute': 'get attribute',
      
      // Wait Types
      'wait': 'wait',
      'implicitly': 'implicit wait',
      'implicitwait': 'implicit wait',
      'explicit': 'explicit wait',
      'explictwait': 'explicit wait',
      'fluent': 'fluent wait',
      'fluentwait': 'fluent wait',
      
      // Other Common Typos
      'dataprovider': 'data provider',
      'parameterized': 'parameterized test',
      'paramaterized': 'parameterized test',
      'annotation': 'annotation',
      'anotation': 'annotation',
      'listener': 'listener',
      'listner': 'listener',
      'report': 'report',
      'logger': 'logger',
      'logging': 'logging',
      'debugging': 'debugging',
      'debuging': 'debugging',
    };

    // Return original query if empty
    if (!query || !query.trim()) {
      console.log('📝 Input query is empty or null, skipping correction');
      return query;
    }

    console.log('🔍 Starting query correction process...');
    let corrected = query.toLowerCase().trim();
    const originalQuery = query;
    
    // Step 1: Remove special characters (?, !, @, #, $, %, ^, &, *, etc.)
    // Keep alphanumeric, spaces, and hyphens (for compound words)
    const beforeSpecialCharRemoval = corrected;
    corrected = corrected.replace(/[^a-z0-9\s\-]/gi, '');
    
    // Log special character removal if characters were removed
    if (beforeSpecialCharRemoval !== corrected) {
      const removedChars = beforeSpecialCharRemoval.replace(/[a-z0-9\s\-]/gi, '').split('').filter((v, i, a) => a.indexOf(v) === i);
      console.log('🧹 Special characters removed:', removedChars.join(''));
      console.log('   Before: "' + beforeSpecialCharRemoval + '" → After: "' + corrected + '"');
    }
    
    // Step 2: Normalize spaces after special character removal
    corrected = corrected.replace(/\s+/g, ' ').trim();
    
    // Step 3: Handle empty string after removal
    if (!corrected || corrected.length === 0) {
      console.warn('⚠️ Query became empty after special character removal');
      console.warn('   Original query: "' + originalQuery + '" → Using original query instead');
      return originalQuery;
    }
    
    // Step 4: Apply corrections from dictionary
    let correctionsApplied = [];
    const beforeCorrections = corrected;
    
    for (const [misspelled, correct] of Object.entries(corrections)) {
      const regex = new RegExp(`\\b${misspelled}\\b`, 'gi');
      if (regex.test(corrected)) {
        const count = (corrected.match(regex) || []).length;
        corrected = corrected.replace(regex, correct);
        correctionsApplied.push({
          from: misspelled,
          to: correct,
          count: count
        });
      }
    }
    
    // Log typo corrections if any were applied
    if (correctionsApplied.length > 0) {
      console.log('📌 Typo corrections applied (' + correctionsApplied.length + ' corrections):');
      correctionsApplied.forEach((correction) => {
        console.log('   ✓ "' + correction.from + '" → "' + correction.to + '" (found ' + correction.count + 'x)');
      });
      console.log('   Before: "' + beforeCorrections + '" → After: "' + corrected + '"');
    }
    
    // Step 5: Normalize spaces again after corrections
    corrected = corrected.replace(/\s+/g, ' ').trim();
    
    // Step 6: Validate that corrected query is not empty
    if (!corrected || corrected.length === 0) {
      console.warn('⚠️ Corrected query became empty after typo corrections');
      console.warn('   Original query: "' + originalQuery + '" → Using original query instead');
      return originalQuery;
    }
    
    // Step 7: Capitalize first letter and log correction for debugging
    const result = corrected.charAt(0).toUpperCase() + corrected.slice(1);
    
    // Log final result
    if (result.toLowerCase() !== originalQuery.toLowerCase()) {
      console.log('✨ Query correction successful');
      console.log('   Before: "' + originalQuery + '"');
      console.log('   After:  "' + result + '"');
    } else {
      console.log('✓ Query verified - no corrections needed');
    }
    
    return result;
  };

  const getAnswerFromAI = async () => {
    if (!searchQuery.trim()) {
      alert('Please enter a question first');
      return;
    }

    try {
      setAiLoading(true);
      
      // Auto-correct the query
      const correctedQuery = autoCorrectQuery(searchQuery);
      console.log('Original:', searchQuery, '-> Corrected:', correctedQuery);
      
      const profileParam = profile?.id ? `&profileId=${encodeURIComponent(profile.id)}` : '';
      const response = await axios.get(
        `${API_BASE_URL}/api/ai/answer?q=${encodeURIComponent(correctedQuery)}&customPrompt=${encodeURIComponent(customPrompt || '')}${profileParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('AI Response:', response.data);
      
      // Store raw answer, only format for display
      setQuestion({
        question: correctedQuery,
        answer: response.data.data.answer,
        isFromAI: true,
        isCached: response.data.data.cached || false
      });
      setSuggestions([]);
      
      // Broadcast to session if one is active
      if (sessionId && socketRef.current) {
        console.log('📡 Broadcasting answer to session:', sessionId);
        socketRef.current.emit('trainer:update-answer', {
          question: correctedQuery,
          answer: response.data.data.answer
        });
      }
    } catch (err) {
      console.error('Error getting AI answer:', err);
      if (handleInvalidToken(err)) {
        setAiLoading(false);
        return;
      }
      alert('Error getting answer from AI. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleCreateNewSession = async () => {
    if (!window.confirm('This will end your current session and create a new one. Continue?')) {
      return;
    }

    try {
      setIsStartingSession(true);
      console.log('🚀 Creating new session (ending current one)...');
      
      const response = await axios.post(
        `${API_BASE_URL}/api/sessions/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const newSessionId = response.data.data.sessionId;
        const newShareUrl = response.data.data.shareUrl;
        
        // Update localStorage with new session
        localStorage.setItem('activeSessionId', newSessionId);
        localStorage.setItem('activeShareUrl', newShareUrl);
        
        setSessionId(newSessionId);
        setShareUrl(newShareUrl);
        
        console.log('✅ New session created:', newSessionId);
        console.log('   Share URL:', newShareUrl);
      }
    } catch (error) {
      console.error('Error creating new session:', error);
      alert('Error creating new session. Please try again.');
    } finally {
      setIsStartingSession(false);
    }
  };

  const handleStartSession = async () => {
    try {
      // Check if there's already an active session in localStorage
      const existingSessionId = localStorage.getItem('activeSessionId');
      const existingShareUrl = localStorage.getItem('activeShareUrl');
      
      if (existingSessionId && existingShareUrl) {
        // Session already exists - just show it instead of creating a new one
        console.log('✓ Active session already exists:', existingSessionId);
        setSessionId(existingSessionId);
        setShareUrl(existingShareUrl);
        setShowSessionModal(true);
        return;
      }
      
      setIsStartingSession(true);
      
      // Check server for existing active session before creating new one
      try {
        const activeCheck = await axios.get(
          `${API_BASE_URL}/api/sessions/trainer/active`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (activeCheck.data.success && activeCheck.data.data) {
          // Trainer already has an active session on server - restore it
          const existingData = activeCheck.data.data;
          console.log('✓ Found existing active session on server:', existingData.sessionId);
          
          localStorage.setItem('activeSessionId', existingData.sessionId);
          localStorage.setItem('activeShareUrl', existingData.shareUrl);
          
          setSessionId(existingData.sessionId);
          setShareUrl(existingData.shareUrl);
          setShowSessionModal(true);
          setIsStartingSession(false);
          return;
        }
      } catch (checkErr) {
        // 404 means no active session - that's fine, proceed to create one
        if (checkErr.response?.status !== 404) {
          console.warn('Error checking active session:', checkErr.message);
        }
      }
      
      console.log('🚀 Starting new session...');
      
      const response = await axios.post(
        `${API_BASE_URL}/api/sessions/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const newSessionId = response.data.data.sessionId;
        const newShareUrl = response.data.data.shareUrl;
        
        // Persist to localStorage
        localStorage.setItem('activeSessionId', newSessionId);
        localStorage.setItem('activeShareUrl', newShareUrl);
        
        setSessionId(newSessionId);
        setShareUrl(newShareUrl);
        setShowSessionModal(true);
        
        console.log('✅ Session created:', newSessionId);
        console.log('   Share URL:', newShareUrl);
        console.log('   Socket will connect in useEffect (triggered by sessionId change)');
      }
    } catch (error) {
      console.error('Error starting session:', error);
      alert('Error starting session. Please try again.');
    } finally {
      setIsStartingSession(false);
    }
  };

  const handleCopyShareUrl = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      alert('Share URL copied to clipboard!');
    }
  };

  const handleEndSession = async () => {
    if (!sessionId) {
      console.warn('No active session to end');
      return;
    }
    
    try {
      console.log('🛑 Ending session:', sessionId);
      const response = await axios.post(
        `${API_BASE_URL}/api/sessions/${sessionId}/end`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('✅ End session response:', response.data);
      
      // Clear from localStorage
      localStorage.removeItem('activeSessionId');
      localStorage.removeItem('activeShareUrl');
      
      setSessionId(null);
      setShareUrl(null);
      setShowSessionModal(false);
      console.log('✓ Session ended successfully');
    } catch (error) {
      console.error('❌ Error ending session:', {
        status: error.response?.status,
        message: error.response?.data?.message,
        error: error.message
      });
      alert(`Error ending session: ${error.response?.data?.message || error.message}`);
    }
  };

  // Chat Feature Functions
  const scrollChatToBottom = () => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollChatToBottom();
  }, [chatMessages]);

  const handleSendChatMessage = async () => {
    if (!chatInput.trim()) return;

    try {
      setChatLoading(true);
      
      // Store the message before clearing input
      const messageToSend = chatInput;
      
      // Add user message to chat
      const userMessage = {
        role: 'user',
        content: messageToSend,
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, userMessage]);
      setChatInput('');

      // Send to backend
      const response = await axios.post(
        `${API_BASE_URL}/api/chat/send`,
        {
          message: messageToSend,
          sessionId: sessionId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const aiResponse = response.data.data.response;

      // Add AI response to chat
      const aiMessage = {
        role: 'assistant',
        content: aiResponse,
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, aiMessage]);

      console.log('✅ Chat message sent and response received');

      // Broadcast to session students in real-time
      if (sessionId && socketRef.current?.connected) {
        console.log('📡 Broadcasting chat response to session:', sessionId);
        socketRef.current.emit('trainer:update-answer', {
          question: question || 'Chat Response',
          answer: aiResponse
        });
      }
    } catch (error) {
      console.error('❌ Chat error:', error);
      if (handleInvalidToken(error)) {
        setChatLoading(false);
        return;
      }
      alert('Error sending chat message. Please try again.');
    } finally {
      setChatLoading(false);
    }
  };

  const handleClearChatHistory = async () => {
    if (!window.confirm('Are you sure you want to clear all chat messages?')) return;

    try {
      await axios.post(
        `${API_BASE_URL}/api/chat/clear`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChatMessages([]);
      console.log('✅ Chat history cleared');
    } catch (error) {
      console.error('❌ Error clearing chat:', error);
      if (handleInvalidToken(error)) return;
      alert('Error clearing chat history.');
    }
  };

  const handleChatMicDown = async () => {
    if (chatLoading || chatVoiceListening) return;
    
    try {
      const transcriber = new WhisperTranscriber();
      chatMediaRecorderRef.current = transcriber;
      
      await transcriber.startRecording();
      setChatVoiceListening(true);
    } catch (error) {
      console.error('❌ Microphone error:', error);
      alert(`Microphone error: ${error.message}`);
      setChatVoiceListening(false);
    }
  };

  const handleChatMicUp = async () => {
    if (!chatVoiceListening) return;

    try {
      setChatLoading(true);
      const audioBlob = await chatMediaRecorderRef.current.stopRecording();
      console.log('🔄 Transcribing chat voice with Whisper...');
      
      const transcribedText = await chatMediaRecorderRef.current.transcribeAudio(audioBlob, token);
      console.log(`✨ Chat transcribed: "${transcribedText}"`);
      
      setChatInput(transcribedText);
      setChatVoiceListening(false);
      setChatLoading(false);
    } catch (error) {
      console.error('❌ Chat transcription failed:', error);
      alert(`Transcription error: ${error.message}`);
      setChatVoiceListening(false);
      setChatLoading(false);
    }
  };

  // Desktop: click to start/stop chat recording (toggle)
  const handleChatMicClick = async () => {
    if (chatLoading) return;
    if (chatVoiceListening) {
      await handleChatMicUp();
    } else {
      await handleChatMicDown();
    }
  };

  // Handle when user moves mouse/pointer away from button while holding (cancel recording - mobile only)
  const handleChatMicLeave = async () => {
    if (chatVoiceListening && chatMediaRecorderRef.current) {
      try {
        chatMediaRecorderRef.current.abortRecording();
        setChatVoiceListening(false);
      } catch (error) {
        console.error('❌ Error canceling recording:', error);
      }
    }
  };

  return (
    <div className="dashboard">
      {/* Modern Header */}
      <div className="header-bar">
        <div className="logo-title">
          <h1>Find QA Answers</h1>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#ffffff', fontWeight: '500' }}>MR. VELMURUGAN - 99441 52058</p>
          {(profile || interviewContext) && (
            <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: 'rgba(255,255,255,0.9)', fontWeight: '400' }}>
              {profile?.studentName && <span>👤 {profile.studentName}</span>}
              {interviewContext?.companyName && (
                <>
                  {profile?.studentName && <span> • </span>}
                  <span>🏢 {interviewContext.companyName} - {interviewContext.interviewRound}</span>
                </>
              )}
            </p>
          )}
        </div>
        <div className="header-actions">
          <button className="btn-add" onClick={() => setShowAddModal(true)}>
            Add Question
          </button>
          <button className="btn-my-questions" onClick={fetchMyQuestions}>
            My Questions
          </button>
          {!sessionId && (
            <button 
              className="btn-start-session"
              onClick={handleStartSession}
              disabled={isStartingSession}
              title="Start a live session to share with students"
            >
              {isStartingSession ? 'Starting...' : '📡 Start Session'}
            </button>
          )}
          {sessionId && (
            <button 
              className="btn-session-active"
              onClick={() => setShowSessionModal(true)}
              title="Session is active. Click to manage"
            >
              🟢 Live Session
            </button>
          )}
          {profile && (
            <button 
              className="btn-back-to-profile"
              onClick={() => window.history.back()}
              title="Back to Profile"
            >
              ← Back to Profile
            </button>
          )}
          <button className="btn-logout" onClick={onLogout}>
            Logout
          </button>
        </div>
      </div>

      <div className="main-container" style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'flex-start' }}>
        {/* Left Sidebar - Starred Questions */}
        <div className={`sidebar-starred ${starredExpanded ? 'expanded' : ''}`}>
          <div className="sidebar-header" onClick={() => setStarredExpanded(!starredExpanded)}>
            <h3>
              <span className="star-icon">⭐</span>
              Starred
            </h3>
            <span className="starred-toggle">
              {starredExpanded ? '−' : '+'}
            </span>
          </div>
          {starredQuestions.length > 0 ? (
            <ul className="starred-list">
              {starredQuestions.map((q) => (
                <li
                  key={q.id}
                  className={`starred-item ${question?.id === q.id ? 'active' : ''}`}
                  onClick={() => {
                    setQuestion(q);
                    
                    // Broadcast to session if one is active
                    if (sessionId && socketRef.current) {
                      console.log('📡 Broadcasting starred answer to session:', sessionId);
                      socketRef.current.emit('trainer:update-answer', {
                        question: q.question,
                        answer: q.answer
                      });
                    }
                    
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  title={q.question}
                >
                  <span className="starred-item-text">{q.question}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="empty-starred">
              No starred questions yet<br/>
              <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>Click the star icon to save</span>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="main-content" style={{ flex: 1 }}>

      <div className="container" style={{ paddingTop: '16px' }}>
          {/* Custom Prompt Button & Chat Button */}
          <div style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
            <button 
              onClick={() => setShowPromptEditor(!showPromptEditor)}
              style={{ padding: '8px 16px', background: 'var(--primary, #1e3a8a)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
              title="Customize AI system prompt by role (QA, Developer, SDET, etc.)"
            >
              ⚙️ AI Prompt
            </button>
            <button 
              onClick={() => setShowChatModal(true)}
              style={{ padding: '8px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
              title="Open chat for interactive Q&A"
            >
              💬 Chat
            </button>
          </div>

          {/* Custom Prompt Editor Modal */}
          {showPromptEditor && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: 'white', borderRadius: '8px', padding: '24px', maxWidth: '600px', width: '90%', maxHeight: '80vh', overflow: 'auto' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>Customize AI System Prompt</h3>
                <textarea
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="Enter custom AI system prompt... (e.g., 'You are a Java developer answering questions...')"
                  style={{ width: '100%', padding: '12px', minHeight: '200px', border: '1px solid #d1d5db', borderRadius: '4px', fontFamily: 'monospace', fontSize: '12px', marginBottom: '16px', boxSizing: 'border-box' }}
                />
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button 
                    onClick={() => setShowPromptEditor(false)}
                    style={{ padding: '8px 16px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={saveCustomPrompt}
                    style={{ padding: '8px 16px', background: 'var(--primary, #1e3a8a)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
                  >
                    Save Prompt
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modern Search */}
          <div className="search-section">
            {/* Search Bar */}
            <div className="search-bar-modern">
              <input
                type="text"
                placeholder={isListening ? "🎤 Recording... Click to stop" : "Search your question"}
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (suggestions.length > 0) {
                      // If suggestions exist, select the first one
                      fetchQuestion(suggestions[0].question);
                    } else if (searchQuery.trim()) {
                      // If no suggestions, get answer from AI
                      getAnswerFromAI();
                    }
                  }
                }}
                autoComplete="off"
                className="search-input"
                disabled={isListening}
              />
              <button 
                className={`voice-btn ${isListening ? 'listening' : ''}`}
                {...(isMobile
                  ? {
                      onTouchStart: (e) => { e.preventDefault(); handleSearchMicDown(); },
                      onTouchEnd: (e) => { e.preventDefault(); handleSearchMicUp(); },
                      onMouseDown: handleSearchMicDown,
                      onMouseUp: handleSearchMicUp,
                      onMouseLeave: handleSearchMicLeave,
                    }
                  : { onClick: handleSearchMicClick }
                )}
                title={isMobile ? "Press and hold to record" : (isListening ? "Click to stop" : "Click to record")}
              >
                {isListening ? '⏹ Stop' : 'Voice'}
              </button>
              <button 
                className="ai-btn"
                onClick={getAnswerFromAI}
                disabled={aiLoading || !searchQuery.trim()}
                title="Get answer from AI"
              >
                {aiLoading ? 'Loading...' : 'AI Answer'}
              </button>
              {searchQuery && (
                <button 
                  className="clear-btn" 
                  onClick={() => handleSearchChange('')}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Suggestions List - Modern Cards */}
          {suggestions.length > 0 && (
            <div className="suggestions-section">
              <h3 className="section-title">Search Results</h3>
              <ul className="suggestions-list">
                {suggestions.map((s, i) => (
                  <li 
                    key={i} 
                    className="suggestion-card"
                    onClick={() => {
                      console.log('🔍 Search result clicked:', s);
                      console.log('   Question:', s.question);
                      console.log('   Answer exists:', !!s.answer);
                      console.log('   Answer length:', s.answer?.length || 0);
                      
                      setQuestion(s);
                      setSuggestions([]);
                      
                      // Broadcast selected question to session if active
                      console.log('📊 About to broadcast search result:', {
                        question: s.question,
                        answerExists: !!s.answer,
                        answerLength: s.answer?.length,
                        sessionId: sessionId,
                        socketConnected: socketRef.current?.connected,
                        socketId: socketRef.current?.id
                      });
                      
                      if (sessionId && socketRef.current?.connected) {
                        console.log('📡 Emitting trainer:update-answer to session:', sessionId);
                        console.log('   Payload:', {
                          question: s.question,
                          answer: s.answer ? `[${s.answer.length} chars]` : 'MISSING'
                        });
                        
                        socketRef.current.emit('trainer:update-answer', {
                          question: s.question,
                          answer: s.answer
                        });
                        console.log('✅ Search result broadcast emitted');
                      } else {
                        console.warn('⚠️ Cannot broadcast search result:', {
                          reason: !sessionId ? 'No session ID' : 'Socket not connected',
                          sessionId,
                          socketConnected: socketRef.current?.connected
                        });
                      }
                    }}
                  >
                    <div className="suggestion-content">
                      <div className="suggestion-title">{s.question}</div>
                    </div>
                    <div className="suggestion-arrow">→</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Question Details - Modern Card */}
          {question && (
            <div className="details-section">
              <div className="details-card">
                <div className="details-header">
                  <div>
                    <h3 className="question-title">{question.question}</h3>
                    {question.isFromAI && (
                      <span className="ai-badge">
                        {question.isCached ? 'Cached from AI' : 'Powered by OMR Branch AI'}
                      </span>
                    )}
                  </div>
                  <div className="details-actions">
                    <button 
                      className={`star-button ${question.is_starred ? 'starred' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleQuestionStar(question.id, e);
                      }}
                      title={question.is_starred ? 'Remove from starred' : 'Add to starred'}
                      type="button"
                    >
                      {question.is_starred ? '⭐' : '☆'}
                    </button>
                    <button 
                      className="edit-btn" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openEditModal();
                      }}
                      title="Edit answer"
                      type="button"
                    >
                      Edit
                    </button>
                    <button 
                      className="close-details-btn" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setQuestion(null);
                        // Clear question/answer in student session
                        if (sessionId && socketRef.current?.connected) {
                          console.log('📡 Clearing session content for students');
                          socketRef.current.emit('trainer:update-answer', {
                            question: null,
                            answer: null
                          });
                        }
                      }}
                      title="Close"
                      type="button"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                <div className="divider"></div>
                <div className="details-body" dangerouslySetInnerHTML={{ __html: formatAnswer(question.answer) }} />
              </div>
            </div>
          )}

          {/* Empty State */}
          {!question && !searchQuery && (
            <div className="empty-state">
              <h3>Start your search</h3>
              <p>Type a topic to find QA answers</p>
            </div>
          )}

          {/* No Results State */}
          {searchQuery && suggestions.length === 0 && !question && !isLoading && (
            <div className="empty-state">
              <h3>No results found</h3>
              <p>Try searching with different keywords</p>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading...</p>
            </div>
          )}
        </div>
      </div>
        </div>

      {/* Add Question Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => { setShowAddModal(false); setStarNewQuestion(false); }}>
          <div className="modal-box modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Question</h2>
              <button className="modal-close" onClick={() => { setShowAddModal(false); setStarNewQuestion(false); }}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Question</label>
                <input
                  type="text"
                  placeholder="Enter your question"
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Answer</label>
                <RichTextEditor
                  value={formData.answer}
                  onChange={(value) => setFormData({ ...formData, answer: value })}
                  placeholder="Enter the answer"
                />
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="star-new-question"
                  checked={starNewQuestion}
                  onChange={(e) => setStarNewQuestion(e.target.checked)}
                  style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                />
                <label htmlFor="star-new-question" style={{ cursor: 'pointer', margin: 0, fontSize: '0.95rem' }}>
                  ⭐ Star this question
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => { setShowAddModal(false); setStarNewQuestion(false); }}>
                Cancel
              </button>
              <button className="btn-save" onClick={saveQuestion}>
                Save Question
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Question Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-box modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Question</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Question</label>
                <input
                  type="text"
                  placeholder="Enter your question"
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Answer</label>
                <RichTextEditor
                  value={formData.answer}
                  onChange={(value) => setFormData({ ...formData, answer: value })}
                  placeholder="Enter the answer"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-delete" 
                onClick={deleteCurrentQuestion}
                style={{ marginRight: 'auto' }}
              >
                Delete
              </button>
              <button className="btn-cancel" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button className="btn-save" onClick={saveEditedQuestion}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* My Questions Modal */}
      {showMyQuestions && (
        <div className="modal-overlay" onClick={() => setShowMyQuestions(false)}>
          <div className="modal-box" style={{ width: '90%', maxWidth: '900px', height: '80vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>My Questions</h2>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.9rem', color: '#666666', fontWeight: '400' }}>{user}</p>
              </div>
              <button className="modal-close" onClick={() => setShowMyQuestions(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
              {myQuestions.length > 0 ? (
                <div style={{ padding: '20px' }}>
                  {myQuestions.map((q, idx) => (
                    <div key={q.id} style={{ marginBottom: '15px', borderBottom: '1px solid #e5e7eb', paddingBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: '500', cursor: 'pointer', color: 'var(--primary)' }} onClick={() => { setQuestion(q); setShowMyQuestions(false); }}>
                          {idx + 1}. {q.question}
                        </p>
                        <p style={{ margin: '0', fontSize: '0.9rem', color: 'var(--text-light)' }}>Click to view answer</p>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', marginLeft: '12px' }}>
                        <button 
                          className={`star-button ${q.is_starred ? 'starred' : ''}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleQuestionStar(q.id, e);
                          }}
                          title={q.is_starred ? 'Remove from starred' : 'Add to starred'}
                          style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', padding: '4px 8px' }}
                          type="button"
                        >
                          {q.is_starred ? '⭐' : '☆'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  <p>No questions added yet.</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowMyQuestions(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session Sharing Modal */}
      {showSessionModal && sessionId && (
        <div className="modal-overlay" onClick={() => setShowSessionModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Live Session Active</h2>
              <button className="modal-close" onClick={() => setShowSessionModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ textAlign: 'center' }}>
                <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.95rem' }}>
                  Share this link with students to view your live answers:
                </p>
                
                <div style={{ display: 'flex', gap: '8px', marginBottom: '1.5rem' }}>
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      color: '#001f3f'
                    }}
                  />
                  <button
                    className="btn-save"
                    onClick={handleCopyShareUrl}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    📋 Copy
                  </button>
                </div>

                <div style={{ 
                  background: '#f0f7ff',
                  border: '2px solid #001f3f',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  textAlign: 'left'
                }}>
                  <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', fontWeight: '600', color: '#001f3f' }}>
                    📌 How to Share:
                  </p>
                  <ul style={{ margin: '0', paddingLeft: '1.5rem', color: '#666', fontSize: '0.9rem' }}>
                    <li>Copy the link above</li>
                    <li>Send it to students via chat, email, or QR code</li>
                    <li>They'll see a live view of your answers</li>
                    <li>Content updates in real-time</li>
                  </ul>
                </div>

                <p style={{ color: '#999', fontSize: '0.85rem', margin: '0 0 1rem 0' }}>
                  Session ID: <code style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: '3px', color: '#001f3f' }}>{sessionId}</code>
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-save"
                onClick={handleCreateNewSession}
                disabled={isStartingSession}
                title="Create a new session (current one will be ended)"
              >
                ➕ New Session
              </button>
              <button 
                className="btn-delete"
                onClick={handleEndSession}
              >
                🔴 End Session
              </button>
              <button className="btn-cancel" onClick={() => setShowSessionModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      {showChatModal && (
        <div className="modal-overlay" onClick={() => setShowChatModal(false)}>
          <div 
            className="modal-box" 
            onClick={(e) => e.stopPropagation()}
            style={{ 
              width: '95%', 
              maxWidth: '900px', 
              height: '85vh', 
              display: 'flex', 
              flexDirection: 'column',
              maxHeight: '85vh'
            }}
          >
            <div className="modal-header">
              <h2>Chat with AI</h2>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={handleClearChatHistory}
                  style={{
                    padding: '6px 8px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '1.1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Clear chat history"
                >
                  🗑️
                </button>
                <button className="modal-close" onClick={() => setShowChatModal(false)}>✕</button>
              </div>
            </div>
            
            <div 
              className="modal-body" 
              style={{ 
                flex: 1, 
                overflowY: 'auto', 
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                padding: '16px',
                background: '#f9fafb'
              }}
            >
              {chatMessages.length === 0 ? (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%',
                  color: '#999',
                  fontSize: '0.95rem'
                }}>
                  <p>Start a conversation! Ask any QA testing questions.</p>
                </div>
              ) : (
                chatMessages.map((msg, idx) => (
                  <div 
                    key={idx}
                    style={{
                      display: 'flex',
                      justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                      marginBottom: '8px'
                    }}
                  >
                    <div
                      className="chat-message-content"
                      style={{
                        maxWidth: '85%',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        background: msg.role === 'user' ? '#001f3f' : '#e0e7ff',
                        color: msg.role === 'user' ? 'white' : '#001f3f',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        fontSize: '0.95rem',
                        lineHeight: '1.5'
                      }}
                    >
                      {msg.role === 'user' ? (
                        msg.content
                      ) : (
                        <div dangerouslySetInnerHTML={{ __html: formatAnswer(msg.content) }} />
                      )}
                    </div>
                  </div>
                ))
              )}
              <div ref={chatMessagesEndRef} />
            </div>

            <div 
              className="modal-footer"
              style={{
                display: 'flex',
                gap: '8px',
                padding: '12px 16px',
                borderTop: '1px solid #e5e7eb',
                background: 'white',
                alignItems: 'center',
                flexDirection: 'row'
              }}
            >
              <button
                {...(isMobile
                  ? {
                      onTouchStart: (e) => { e.preventDefault(); handleChatMicDown(); },
                      onTouchEnd: (e) => { e.preventDefault(); handleChatMicUp(); },
                      onMouseDown: handleChatMicDown,
                      onMouseUp: handleChatMicUp,
                      onMouseLeave: handleChatMicLeave,
                    }
                  : { onClick: handleChatMicClick }
                )}
                disabled={chatLoading}
                style={{
                  padding: '8px 8px',
                  background: chatVoiceListening ? '#ef4444' : 'none',
                  border: 'none',
                  cursor: chatLoading ? 'not-allowed' : 'pointer',
                  fontSize: '1.1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: chatVoiceListening ? 'white' : '#001f3f',
                  flexShrink: 0,
                  borderRadius: '4px',
                  transition: 'all 0.1s ease',
                  userSelect: 'none'
                }}
                title={isMobile ? "Press and hold to record" : (chatVoiceListening ? "Click to stop" : "Click to record")}
              >
                {chatVoiceListening ? '⏹️' : '🎤'}
              </button>
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendChatMessage();
                  }
                }}
                placeholder={chatVoiceListening ? "Recording... Click mic to stop" : "Type or speak..."}
                disabled={chatLoading || chatVoiceListening}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  fontFamily: 'inherit',
                  outline: 'none'
                }}
              />
              <button
                onClick={handleSendChatMessage}
                disabled={chatLoading || !chatInput.trim()}
                style={{
                  padding: '10px 10px',
                  background: chatLoading || !chatInput.trim() ? '#ccc' : '#001f3f',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: chatLoading || !chatInput.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '40px',
                  flexShrink: 0
                }}
                title="Send message"
              >
                ↩️
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
